import { customerApi, QueryTag } from '../api';
import {
  AssetListScenarios,
  PerformanceGIAssetsQueryParams,
  PerformanceVIAssetsQueryParams,
  PositionModel,
} from './assetsApi';
import { Feature, Geometry } from 'geojson';
import { getUrlParams, QueryParams } from './helpers';
import { InvestigationStatus } from './investigationsApi';
import { NameIdBasicModel } from './commonModels';
import { FenceType } from './geofencesApi';
import { ObjectTypeId } from '@dmc/ui';

export enum VILevel {
  Aware = 'Aware',
  Alert = 'Alert',
  Alarm = 'Alarm',
}

export enum VICategory {
  Collision = 'Collision',
  Tailgating = 'Tailgating',
}

export enum VIType {
  Unknown = 'Unknown',
  CollisionAware = 'CollisionAware',
  CollisionAlert = 'CollisionAlert',
  CollisionAlarm = 'CollisionAlarm',
  TailgatingAware = 'TailgatingAware',
  TailgatingAlert = 'TailgatingAlert',
  TailgatingAlarm = 'TailgatingAlarm',
}

export enum GIType {
  Unknown = 'Unknown',
  GeofenceSpeeding = 'GeofenceSpeeding',
  GeofenceNoGoZoneBreach = 'GeofenceNoGoZoneBreach',
  GeofenceStopSignBreach = 'GeofenceStopSignBreach',
  GeofenceStayedAtLongerThan = 'GeofenceStayedAtLongerThan',
  GeofenceStayedAtLessThan = 'GeofenceStayedAtLessThan',
  GeofenceSpeedingRoadworks = 'GeofenceSpeedingRoadworks',
}

export interface AssetInteractionsQueryModel {
  assetId: string;
  interactionId: string;
}

export interface DotNetixRemoteObjectModel {
  roDN: boolean; // remote object is DotNetix (the roNa field is machine-generated - i.e. not useful)
}

export interface ObjectModel extends NameIdBasicModel {
  // ObjectModel is AssetInteractionObjectBasicDetails on the backend
  spd: number | null;
  minSpd: number | null;
  maxSpd: number | null;
  startPos: PositionModel | null;
  endPos: PositionModel | null;
  ags: number[]; // asset groups
  obj?: ObjectTypeId;
}

// RemoteObjectModel is AssetInteractionRemoteObjectDetails on the backend
export interface RemoteObjectModel extends ObjectModel, DotNetixRemoteObjectModel {}

export interface AssetInteractionListModel
  extends Pick<
      AssetInteractionModelBase,
      'id' | 'ts' | 'it' | 'dur' | 'dist' | 'aud' | 'act' | 'svfix' | 'rn'
    >,
    DotNetixRemoteObjectModel {
  loSpd: number; // local object speed
  roNa: string | null; // remote object name
  roId: number | null; // remote object id
  roSpd: number | null; // remote object speed
  roObj: ObjectTypeId | null; // remote object type
}

export interface VIListModel extends AssetInteractionListModel {
  it: VIType;
}

export interface GIListModel extends AssetInteractionListModel {
  it: GIType;
  geo?: number; // Geofence ID
}

export interface Investigatable {
  invest?: {
    id: number; // investigation id
    st: InvestigationStatus; // investigation status
  } | null;
}

export interface AssetInteractionModelBase extends Investigatable {
  id: string; // interaction id
  ts: string; // timestamp
  it: VIType | GIType; // interaction type
  dur: number | null; // duration (If active is true this is always null. If active is false then this is always a number.)
  dist: number; // distance
  aud: boolean; // audible
  act: boolean; // active
  rn: string | null; // rule name
  lo: ObjectModel; // local object
  ro: RemoteObjectModel | null; // remote object
  svfix: boolean; // Start GPS fix valid
  evfix: boolean; // End GPS fix valid
}

export interface AssetVIModel extends AssetInteractionModelBase {
  it: VIType; // interaction type
}

export interface GeofenceProperties {
  id: number;
  radius?: number;
  name: string;
  fenceType: FenceType;
  slope?: boolean;
  areas?: string;
  ags?: string; // Asset groups
  geofenceType?: string;
}

export const isGeofenceProperties = (obj: unknown): obj is GeofenceProperties => {
  return (
    obj instanceof Object &&
    'id' in obj &&
    typeof obj.id === 'number' &&
    'name' in obj &&
    typeof obj.name === 'string' &&
    'fenceType' in obj &&
    typeof obj.fenceType === 'string'
  );
};

export interface GeofenceBasicModel extends NameIdBasicModel {
  shape?: Feature<Geometry, GeofenceProperties>; // geojson
}

export interface AssetGIModel extends AssetInteractionModelBase {
  it: GIType; // interaction type
  geo?: GeofenceBasicModel | null;
}

export interface GISummaryModel {
  it: GIType;
  cnt: number;
}

export interface VISummaryModel {
  vit: VICategory;
  alarm: number;
  alert: number;
  aware: number;
  cnt: number;
}

export interface InteractionsSummaryModel {
  vi: VISummaryModel[];
  viTot: number;
  gi: GISummaryModel[];
  giTot: number;
}
export interface InteractionsSummaryQueryParams {
  timeRange?: QueryParams['timeRange'];
}

type SummaryQueryType =
  | InteractionsSummaryQueryParams
  | PerformanceVIAssetsQueryParams
  | PerformanceGIAssetsQueryParams;

interface InteractionsSummaryQueryModel {
  query: SummaryQueryType;
}

const isScenarioSummary = (
  query: SummaryQueryType,
): query is Extract<SummaryQueryType, { scenario: AssetListScenarios }> => {
  return 'scenario' in query;
};

export const transformNewInvestigationResponse = (
  response: void,
  meta: { response: Response } | undefined,
): number | null => {
  // 'meta' can be undefined if an error is thrown during the request.
  if (meta) {
    // If this point in the code is reached, the request was successful.
    const locationHeader: string | null = meta.response.headers.get('Location');

    if (locationHeader) {
      // Get the last segment of the Location header (if it exists).
      const investigationId: string | undefined = locationHeader.split('/').pop();

      // Return the investigation ID as a number if it is valid.
      return investigationId ? parseInt(investigationId) : null;
    }
  }

  return null;
};

export const interactionsApi = customerApi.injectEndpoints({
  endpoints: build => {
    return {
      getVehicleInteraction: build.query<AssetVIModel, AssetInteractionsQueryModel>({
        query: ({ assetId, interactionId }) =>
          `api/assets/${assetId}/interactions/${interactionId}`,
        providesTags: (result, error, { interactionId }) =>
          result ? [QueryTag.SiteCache, { type: QueryTag.Interaction, id: interactionId }] : [],
      }),
      getGeofenceInteraction: build.query<AssetGIModel, AssetInteractionsQueryModel>({
        query: ({ assetId, interactionId }) =>
          `api/assets/${assetId}/interactions/${interactionId}`,
        providesTags: (result, error, { interactionId }) =>
          result ? [QueryTag.SiteCache, { type: QueryTag.Interaction, id: interactionId }] : [],
      }),
      getInteractionsSummary: build.query<InteractionsSummaryModel, InteractionsSummaryQueryModel>({
        query: ({ query }) => ({
          url: 'api/site/interactionssummary',
          params: getUrlParams(query),
        }),
        // For query caching, provide a different id based on provided scenario (or its absence).
        providesTags: (result, error, arg) =>
          result
            ? [
                QueryTag.SiteCache,
                {
                  type: QueryTag.InteractionsSummary,
                  id: isScenarioSummary(arg.query) ? arg.query.scenario : 'SITE',
                },
              ]
            : [],
      }),
      investigate: build.mutation<number | null, AssetInteractionsQueryModel>({
        query: ({ assetId, interactionId }) => ({
          url: `api/assets/${assetId}/interactions/${interactionId}/investigate`,
          method: 'POST',
        }),
        transformResponse: transformNewInvestigationResponse,
        invalidatesTags: (result, error, { interactionId }) =>
          !error
            ? [
                { type: QueryTag.Investigation, id: 'LIST' },
                { type: QueryTag.Interaction, id: interactionId },
              ]
            : [{ type: QueryTag.Interaction, id: interactionId }],
      }),
    };
  },
  overrideExisting: false,
});

export const {
  useGetVehicleInteractionQuery,
  useGetGeofenceInteractionQuery,
  useGetInteractionsSummaryQuery,
  useInvestigateMutation,
} = interactionsApi;
